import React, { useRef, useState } from "react";

import { Draggable } from "@hello-pangea/dnd";
import { MoreVert as MoreVertIcon } from "@material-ui/icons";
import classNames from "classnames";

import ActionsDropdown from "./ActionsDropdown";
import SwitchOnOff from "../common/tools/SwitchOnOff";

import type { DropResult } from "@hello-pangea/dnd";
import type { JSX, MouseEvent } from "react";

interface TabSelectorProps {
  index: number;
  selectedIndex: number;
  onSelectTab: (index: number) => void;
  onDragEnd: (result: DropResult) => void;
  onSeasonStatusChange: ({
    seasonId,
    enabled,
  }: {
    seasonId: string;
    enabled: boolean;
  }) => void;
  itemProps: {
    label: string;
    inputValue: number;
    onInputChange: (index: number, value: string) => void;
    data: {
      uuid: string;
      enabled: boolean;
    };
  };
  actions: (index: number, data: unknown) => JSX.Element;
  tabSelectorClassName?: string;
}

function TabSelector({
  index,
  onSelectTab,
  onDragEnd,
  selectedIndex,
  itemProps,
  actions,
  onSeasonStatusChange,
  tabSelectorClassName,
}: TabSelectorProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  const tabSelectorRef = useRef<HTMLDivElement>(null);
  const tabSelectorPosition = tabSelectorRef.current?.getBoundingClientRect();
  const seasonId = itemProps.data.uuid;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setIsOpen(false);
  };

  const tabSelectorClassNames = classNames(
    "tab-selector h-full",
    index === selectedIndex && "selected",
    tabSelectorClassName,
  );

  const tabSelectorStyle = {
    backgroundColor: index === selectedIndex ? "var(--card)" : "var(--muted)",
  };

  return (
    <div ref={tabSelectorRef}>
      <Draggable
        draggableId={`tab-select-${index}`}
        index={index}
        isDragDisabled={!onDragEnd}
      >
        {(draggableProvided) => {
          const { innerRef, draggableProps, dragHandleProps } =
            draggableProvided;
          return (
            <div
              ref={innerRef}
              className="editor-tab-select-item h-full"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...draggableProps}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...dragHandleProps}
              style={draggableProps.style as React.CSSProperties}
            >
              <div
                className={tabSelectorClassNames}
                style={tabSelectorStyle}
                onClick={() => {
                  onSelectTab(index);
                }}
              >
                <div>{itemProps.label}</div>
                <input
                  min="0"
                  type="number"
                  value={itemProps.inputValue}
                  onChange={(e) => {
                    itemProps.onInputChange(index, e.target.value);
                  }}
                />
                <SwitchOnOff
                  checked={itemProps.data.enabled}
                  name={`season-status-${seasonId}`}
                  size="xs"
                  onChange={(enabled: boolean): void =>
                    onSeasonStatusChange({
                      seasonId,
                      enabled,
                    })
                  }
                />
                <div className="tab-icon" onClick={() => setIsOpen(!isOpen)}>
                  <MoreVertIcon />
                </div>
                {isOpen && tabSelectorPosition ? (
                  <ActionsDropdown
                    actions={actions}
                    data={itemProps.data}
                    index={index}
                    itemPosition={tabSelectorPosition}
                    onClick={handleClick}
                  />
                ) : null}
              </div>
            </div>
          );
        }}
      </Draggable>
    </div>
  );
}

export default TabSelector;
